import React from 'react';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface MeasureGridSkeletonProps {
  cards?: number;
  columns?: number;
  className?: string;
}

export function MeasureGridSkeleton({ cards = 6, columns = 3, className }: MeasureGridSkeletonProps) {
  // Use predefined classes for Tailwind to properly include them
  const gridClass = columns === 2 
    ? "grid gap-4 md:grid-cols-2" 
    : columns === 4 
    ? "grid gap-4 md:grid-cols-2 lg:grid-cols-4"
    : "grid gap-4 md:grid-cols-2 lg:grid-cols-3"; // default to 3
    
  return (
    <div className={cn(gridClass, className)}>
      {[...Array(cards)].map((_, index) => (
        <Card key={index}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  {/* Measure ID and Checkbox */}
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-4 w-4 rounded-sm" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                  
                  {/* Title */}
                  <Skeleton className="h-4 w-full max-w-[250px]" />
                  
                  {/* Badges */}
                  <div className="flex gap-2 mt-2">
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-5 w-24 rounded-full" />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Description */}
              <div className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-4/5" />
              </div>
            </CardContent>
          </Card>
      ))}
    </div>
  );
}

// Skeleton for the configuration tabs and settings
export function ConfigurationSettingsSkeleton() {
  return (
    <>
      {/* Configuration Notice Skeleton */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <Skeleton className="w-5 h-5 rounded" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
          </div>
        </div>
      </div>

      {/* Grid of Cards Skeleton */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Practice Profile Card */}
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-32 mb-2" />
              <Skeleton className="h-3 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
            </CardContent>
          </Card>

        {/* MIPS Categories Card */}
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-40 mb-2" />
              <Skeleton className="h-3 w-56" />
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Reweight Scenario */}
              <div className="space-y-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
              
              {/* Category Weights */}
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <div className="grid gap-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center justify-between rounded bg-muted p-2">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-6 w-12 rounded-md" />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
      </div>

      {/* Action Buttons Skeleton */}
      <div className="flex items-center justify-end gap-2 mt-6">
        <Skeleton className="h-10 w-32 rounded-md" />
        <Skeleton className="h-10 w-40 rounded-md" />
      </div>
    </>
  );
}
